import {
  MediaDeviceFailure,
  Participant,
  RemoteParticipant,
  Room,
  RoomEvent,
} from "livekit-client";
import { useEffect, useState } from "react";
import { ROOM_CONFIG } from "../consts";
import { useRoomReconnection } from "./use-reconnection";
import { useTrackManagement } from "./use-track";

export const useRoom = () => {
  const [room] = useState<Room>(new Room(ROOM_CONFIG));
  const [roomName, setRoomName] = useState(
    Math.random().toString(36).substring(2, 10)
  );
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [activeSpeakers, setActiveSpeakers] = useState<Participant[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const { hostTracks } = useTrackManagement(room);
  useRoomReconnection(room);

  const onActiveSpeakersChanged = (speakers: Participant[]) => {
    setActiveSpeakers(speakers);
  };

  const onParticipantConnected = (participant: RemoteParticipant) => {
    setParticipants(prev => [...prev, participant]);
    console.log("Livekit participant connected", participant);
  };

  const onParticipantDisconnected = (participant: RemoteParticipant) => {
    setParticipants(prev => prev.filter(p => p.sid !== participant.sid));
    console.log("Livekit participant left", participant);
  };

  const connect = async (url: string, token: string) => {
    if (!room) return;

    try {
      setIsConnecting(true);
      // connect to room
      await room.connect(url, token);

      // Initialize with any existing participants
      setParticipants([...room.remoteParticipants.values()]);
    } catch (err) {
      throw err instanceof Error ? err : new Error("Failed to connect");
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Disconnect your room
   */
  const disconnect = async () => {
    if (!room) return;

    try {
      await room.disconnect();
    } catch (error) {
      throw MediaDeviceFailure.getFailure(error);
    }
  };

  useEffect(() => {
    if (!room) return;

    // set up event listeners
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);

    // clean up event listeners
    return () => {
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
      room.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
      disconnect();
    };
  }, [room]);

  return {
    room,
    roomName,
    participants,
    activeSpeakers,
    isConnecting,
    hostTracks,
    connect,
    disconnect,
    setRoomName,
  };
};
